import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input, Textarea } from '@/components/ui/Input';
import { Lightbox } from '@/components/Lightbox';
import { AvatarChip } from '@/components/AvatarChip';
import { ReportDialog } from '@/features/report/ReportDialog';
import { AppealDialog } from '@/features/report/AppealDialog';
import { container } from '@/services/container';
import { useFeedStore } from '@/store/feed';
import { useChatStore } from '@/store/chat';
import { useSocialStore } from '@/store/social';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import { markTaskDone } from '@/utils/tasks';
import { renderPoster } from '@/utils/sharePoster';
import { PostMediaBlock, PostThumb, usePostMedia } from '@/features/feed/PostMediaBlock';
import { CONTENT_MAX_IMAGES_PER_POST } from '@/config';
import type { Post, RepostRef } from '@shared/types';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';

/** 字符串 → 64 位十六进制（搜索词哈希，明文不上链）。 */
function sha256Hex(text: string): string {
  return bytesToHex(sha256(new TextEncoder().encode(text)));
}

/** 人性化相对时间（刚刚 / N 分钟前 / N 小时前 / 昨天 / N 天前 / 日期）。 */
function formatRelativeTime(ts: number): string {
  if (!Number.isFinite(ts) || ts <= 0) return '';
  const diff = Date.now() - ts;
  const min = 60_000;
  const hour = 3_600_000;
  const day = 86_400_000;
  if (diff < min) return '刚刚';
  if (diff < hour) return `${Math.floor(diff / min)} 分钟前`;
  if (diff < day) {
    const h = Math.floor(diff / hour);
    return h >= 20 ? '昨天' : `${h} 小时前`;
  }
  if (diff < 7 * day) return `${Math.floor(diff / day)} 天前`;
  const d = new Date(ts);
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

/** 链上社交动作统一入口（like/collect/subscribe/share/follow/unfollow）。 */
async function actSocial(
  action: 'like' | 'collect' | 'subscribe' | 'share',
  target: string,
): Promise<void> {
  const addr = container.identity.address;
  if (!addr) throw new MeshLinkError('E_LOCKED');
  await container.chain.socialAct({
    action,
    from: addr as `0x${string}`,
    target,
    signature: container.identity.signObject({ action, from: addr, target, ts: Date.now() }),
  });
}

/** 内容锚定（评论/转发/发布后存证），失败不阻断主流程。 */
async function anchorKind(kind: 'comment' | 'repost' | 'post' | 'image' | 'video' | 'audio' | 'live', cid: string): Promise<boolean> {
  const addr = container.identity.address;
  if (!addr || !cid) return false;
  try {
    await container.chain.anchor({
      author: addr as `0x${string}`,
      cid,
      signature: container.identity.signObject({ kind, cid, ts: Date.now() }),
      kind,
    });
    return true;
  } catch {
    return false;
  }
}

interface CommentItem {
  id: string;
  cid: string;
  author: string;
  text: string;
  ts: number;
}

/** 主操作按钮（点赞/评论/转发/打赏），数字内联，移动端拇指友好。 */
function ActionBtn({
  onClick,
  active,
  accent,
  children,
  disabled,
}: {
  onClick: () => void;
  active?: boolean;
  accent?: boolean;
  children: React.ReactNode;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-1 rounded-full py-2 text-xs transition-colors disabled:opacity-50 ${
        accent ? 'text-accent' : active ? 'text-accent' : 'text-dim hover:text-text'
      }`}
    >
      {children}
    </button>
  );
}

/** v3.27 转发引用卡：展示原帖完整内容（文字/图片/视频/语音，CID 引用拉取）。 */
export function RepostCard({ ref_ }: { ref_: RepostRef }): JSX.Element {
  const [previewCid, setPreviewCid] = useState<string | null>(null);
  const previewUrl = usePostMedia(previewCid ?? '').url;
  const refImages = Array.isArray(ref_.images) ? ref_.images : [];
  return (
    <div className="mx-3 mt-2 rounded-btn bg-panel-2 p-2.5">
      <p className="text-[11px] text-dim">
        🔁 转发自 <span className="font-medium text-text">{ref_.authorName || '匿名用户'}</span>
      </p>
      {ref_.text ? (
        <p className="mt-1 whitespace-pre-wrap text-sm leading-snug text-text line-clamp-3">{ref_.text}</p>
      ) : null}
      {refImages.length > 0 ? (
        <div className={`mt-2 grid gap-1 ${refImages.length === 1 ? 'grid-cols-1' : refImages.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
          {refImages.map((img) => (
            <div key={img.cid} className={refImages.length === 1 ? 'w-full' : 'aspect-square'}>
              <PostThumb
                cid={img.thumbCid || img.cid}
                fullCid={img.cid}
                onPreview={setPreviewCid}
                className={refImages.length === 1 ? 'max-h-52 w-full rounded-lg object-cover' : 'h-full w-full rounded-lg object-cover'}
              />
            </div>
          ))}
        </div>
      ) : null}
      {ref_.media ? (
        <div className="mt-2">
          <PostMediaBlock media={ref_.media} />
        </div>
      ) : null}
      {previewUrl ? <Lightbox url={previewUrl} onClose={() => setPreviewCid(null)} /> : null}
    </div>
  );
}

function PostCard({
  post,
  onReport,
  onAppeal,
}: {
  post: Post;
  onReport: (id: string) => void;
  onAppeal?: (cid: string) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const liked = useFeedStore((s) => !!s.liked[post.id]);
  const stat = useFeedStore((s) => s.stats[post.id] ?? {});
  const [busy, setBusy] = useState<string | null>(null);
  const [commentOpen, setCommentOpen] = useState(false);
  const [previewCid, setPreviewCid] = useState<string | null>(null);
  const previewUrl = usePostMedia(previewCid ?? '').url;
  const [tipOpen, setTipOpen] = useState(false);
  const [tipAmount, setTipAmount] = useState('1');
  const [tipNote, setTipNote] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const authorDid = typeof post.authorDid === 'string' ? post.authorDid : '';
  const manifestCid = typeof post.manifestCid === 'string' ? post.manifestCid : '';
  const images = Array.isArray(post.images) ? post.images : [];
  const media = post.media;
  const anchored = typeof post.blockHeight === 'number' && post.blockHeight > 0;

  const run = async (label: string, fn: () => Promise<void>): Promise<void> => {
    if (busy) return;
    setBusy(label);
    try {
      await fn();
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(null);
    }
  };

  const toggleLike = (): Promise<void> =>
    run('like', async () => {
      const cur = useFeedStore.getState().isLiked(post.id);
      if (!cur && manifestCid) await actSocial('like', manifestCid);
      useFeedStore.getState().setLiked(post.id, !cur);
      useFeedStore.getState().bumpStat(post.id, 'likes', cur ? -1 : 1);
    });

  const onCollect = (): Promise<void> =>
    run('collect', async () => {
      if (!manifestCid) throw new MeshLinkError('E_INVALID_INPUT');
      await actSocial('collect', manifestCid);
      useUiStore.getState().pushToast('success', '已收藏（上链存证）');
      setMoreOpen(false);
    });

  const onTip = (): Promise<void> =>
    run('tip', async () => {
      if (!authorDid) throw new MeshLinkError('E_INVALID_INPUT');
      const toAddr = authorDid.startsWith('did:dpb:') ? authorDid.slice('did:dpb:'.length) : authorDid;
      if (toAddr === (container.identity.address ?? '').toLowerCase()) {
        useUiStore.getState().pushToast('error', '不能给自己打赏');
        return;
      }
      const n = Number(tipAmount);
      if (!Number.isFinite(n) || n <= 0) throw new MeshLinkError('E_INVALID_INPUT', '金额不正确');
      await container.wallet.transfer(toAddr, tipAmount);
      useFeedStore.getState().bumpStat(post.id, 'tips', Math.round(n * 1000) / 1000);
      useUiStore.getState().pushToast(
        'success',
        `已打赏 ${tipAmount} DPB（链上转账${tipNote ? ` · ${tipNote}` : ''}）`,
      );
      setTipOpen(false);
      setTipNote('');
    });

  const onShare = (): Promise<void> =>
    run('share', async () => {
      if (!manifestCid) throw new MeshLinkError('E_INVALID_INPUT');
      await actSocial('share', manifestCid);
      // v3.27 分享携带内容：有文字带文字，有图/视频带首个媒体附件（Web Share Level 2）
      const shareText = post.text || `${post.authorName || 'MeshLink 用户'} 分享了一条内容`;
      let file: File | null = null;
      try {
        const mediaCid = images[0]?.cid ?? media?.cid;
        if (mediaCid) {
          const res = await container.feed.getImage(mediaCid);
          if (res) {
            const isVideo = res.mime.includes('video');
            const isAudio = res.mime.includes('audio');
            const ext = isVideo ? 'mp4' : isAudio ? 'mp3' : 'jpg';
            file = new File([new Uint8Array(res.bytes)], `mesh-${mediaCid.slice(0, 8)}.${ext}`, {
              type: res.mime,
            });
          }
        }
      } catch {
        /* 媒体拉取失败仅降级文本 */
      }
      if (typeof navigator.share === 'function') {
        if (file && typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
          await navigator
            .share({ title: post.authorName || 'MeshLink 内容', text: shareText, files: [file] })
            .catch(() => undefined);
        } else {
          await navigator
            .share({ title: post.authorName || 'MeshLink 内容', text: shareText, url: `https://mesh.doup.cn/feed#${manifestCid}` })
            .catch(() => undefined);
        }
      } else {
        await navigator.clipboard
          ?.writeText(`${shareText}\nhttps://mesh.doup.cn/feed#${manifestCid}`)
          .catch(() => undefined);
        useUiStore.getState().pushToast('success', '原文与链接已复制（分享已上链）');
      }
      setMoreOpen(false);
    });

  /** v3.27 转发：发布引用转发帖（携带原帖文字/图片/视频 CID 引用）+ 原帖计数 + 上链存证。 */
  const onRepost = (): Promise<void> =>
    run('repost', async () => {
      if (!manifestCid) throw new MeshLinkError('E_INVALID_INPUT');
      const ref: RepostRef = {
        cid: manifestCid,
        authorDid,
        authorName: post.authorName || '匿名用户',
        text: post.text || '',
        images: images.length > 0 ? images : undefined,
        media,
      };
      await container.feed.publish({ text: '', repostRef: ref });
      useFeedStore.getState().bumpStat(post.id, 'reposts');
      const ok = await anchorKind('repost', manifestCid);
      useUiStore.getState().pushToast('success', ok ? '已转发（转发帖已发布，上链存证）' : '已转发（转发帖已发布，锚定排队中）');
    });

  const onPoster = (): Promise<void> =>
    run('poster', async () => {
      const url = await renderPoster(post);
      const a = document.createElement('a');
      a.href = url;
      a.download = `meshlink-${post.id || Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
      useUiStore.getState().pushToast('success', '分享海报已生成并下载');
      setMoreOpen(false);
    });

  // 有效浏览埋点（可见 ≥10s 上报 browse）
  const viewRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!anchored || !manifestCid) return;
    const el = viewRef.current;
    if (!el) return;
    const addr = container.identity.address;
    if (!addr) return;
    let startedAt = 0;
    let reported = false;
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !startedAt) {
          startedAt = Date.now();
        } else if (!entry.isIntersecting && startedAt > 0) {
          const dur = (Date.now() - startedAt) / 1000;
          if (!reported && dur >= 10) {
            reported = true;
            useFeedStore.getState().bumpStat(post.id, 'views');
            void container.chain
              .browse({
                from: addr as `0x${string}`,
                cid: manifestCid,
                duration: Math.min(600, Math.round(dur)),
                ratio: dur >= 30 ? 1 : 0.5,
              })
              .catch(() => undefined);
          }
          startedAt = 0;
        }
      },
      { threshold: 0.5 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [anchored, manifestCid]);

  const shortDid = authorDid ? authorDid.replace(/^did:dpb:/, '').slice(-8) : '';
  const textLong = (post.text || '').length > 90;

  return (
    <article ref={viewRef as React.RefObject<HTMLElement>} className="card overflow-hidden">
      {/* 头部：头像 + 昵称 + 相对时间 + 更多 */}
      <header className="flex items-center gap-2 p-3">
        <button
          type="button"
          className="shrink-0"
          onClick={() => navigate(`/u/${encodeURIComponent(authorDid)}`)}
          title="进入创作者主页"
        >
          <AvatarChip did={authorDid} name={post.authorName} size="sm" />
        </button>
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={() => navigate(`/u/${encodeURIComponent(authorDid)}`)}
        >
          <span className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-text">
              {post.authorName || '匿名用户'}
            </span>
            {post.blockHeight ? (
              <span className="shrink-0 rounded-full bg-panel-2 px-1.5 py-0.5 text-[10px] text-dim">
                ⛏ #{post.blockHeight}
              </span>
            ) : null}
          </span>
          <span className="block text-[11px] text-dim">
            {formatRelativeTime(post.ts)}
            {shortDid ? ` · 0x${shortDid}` : ''}
          </span>
        </button>
        <div className="relative">
          <button
            type="button"
            className="rounded-full px-2 py-1 text-lg leading-none text-dim hover:bg-panel-2 hover:text-text"
            onClick={() => setMoreOpen((v) => !v)}
            aria-label="更多操作"
          >
            ⋯
          </button>
          {moreOpen ? (
            <>
              <button
                type="button"
                className="fixed inset-0 z-10 cursor-default"
                onClick={() => setMoreOpen(false)}
                aria-label="关闭菜单"
              />
              <div className="absolute right-0 top-8 z-20 w-36 overflow-hidden rounded-btn border border-line bg-panel shadow-lg">
                <button type="button" className="block w-full px-3 py-2 text-left text-xs text-text hover:bg-panel-2" onClick={() => void onCollect()}>
                  ⭐ 收藏（上链）
                </button>
                <button type="button" className="block w-full px-3 py-2 text-left text-xs text-text hover:bg-panel-2" onClick={() => void onPoster()}>
                  🖼 生成海报
                </button>
                <button type="button" className="block w-full px-3 py-2 text-left text-xs text-err hover:bg-panel-2" onClick={() => { setMoreOpen(false); onReport(post.id); }}>
                  🚩 举报
                </button>
                {onAppeal ? (
                  <button
                    type="button"
                    className="block w-full px-3 py-2 text-left text-xs text-text hover:bg-panel-2"
                    onClick={() => { setMoreOpen(false); onAppeal(post.manifestCid ?? post.id); }}
                  >
                    ⚖ 内容申诉
                  </button>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </header>

      {/* 正文：长文默认两行截断，点「全文」展开 */}
      {post.text ? (
        <p
          className={`whitespace-pre-wrap px-3 text-sm text-text ${!expanded && textLong ? 'line-clamp-2' : ''}`}
          onClick={() => textLong && setExpanded((v) => !v)}
        >
          {post.text}
        </p>
      ) : null}
      {textLong ? (
        <button
          type="button"
          className="px-3 pt-1 text-xs text-accent"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? '收起' : '全文'}
        </button>
      ) : null}

      {/* v3.27 转发引用卡：携带原帖完整内容（文字/图片/视频 CID 引用） */}
      {post.repostRef ? <RepostCard ref_={post.repostRef} /> : null}

      {/* 图片：1 张大图 / 2 张并排 / 3+ 三列网格，点击放大 */}
      {images.length > 0 ? (
        <div className={`mt-2 grid gap-1 px-3 ${images.length === 1 ? 'grid-cols-1' : images.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
          {images.map((img) => (
            <div key={img.cid} className={images.length === 1 ? 'w-full' : 'aspect-square'}>
              <PostThumb
                cid={img.thumbCid || img.cid}
                fullCid={img.cid}
                onPreview={setPreviewCid}
                className={images.length === 1 ? 'max-h-72 w-full rounded-lg object-cover' : 'h-full w-full rounded-lg object-cover'}
              />
            </div>
          ))}
        </div>
      ) : null}

      {media ? (
        <div className="mt-2 px-3">
          <PostMediaBlock media={media} />
        </div>
      ) : null}

      {/* 主操作栏：赞 / 评论 / 转发 / 分享 / 打赏，数字内联 */}
      <footer className="mt-1 flex items-stretch border-t border-line px-2 py-1.5">
        <ActionBtn onClick={() => void toggleLike()} active={liked} disabled={busy === 'like'}>
          {liked ? '❤️' : '♡'} <span className="tabular-nums">{stat.likes ?? 0}</span>
        </ActionBtn>
        <ActionBtn onClick={() => setCommentOpen((v) => !v)}>
          💬 <span className="tabular-nums">{stat.comments ?? 0}</span>
        </ActionBtn>
        <ActionBtn onClick={() => void onRepost()} disabled={busy === 'repost'}>
          🔁 <span className="tabular-nums">{stat.reposts ?? 0}</span>
        </ActionBtn>
        <ActionBtn onClick={() => void onShare()} disabled={busy === 'share'}>
          ↗ 分享
        </ActionBtn>
        <ActionBtn onClick={() => setTipOpen((v) => !v)} accent>
          🎁 {stat.tips ? <span className="tabular-nums">{stat.tips}</span> : '打赏'}
        </ActionBtn>
      </footer>

      {stat.views ? (
        <p className="flex items-center gap-2 px-3 pb-2 text-[11px] text-dim/70">
          <span>👁 {stat.views} 次浏览</span>
          <span className="ml-auto">互动数据为本机聚合</span>
        </p>
      ) : null}

      {/* 打赏面板 */}
      {tipOpen ? (
        <div className="mx-3 mb-3 space-y-2 rounded-btn bg-panel-2 p-3">
          <p className="text-xs font-medium text-text">🎁 打赏给 {post.authorName || '作者'}</p>
          <div className="flex flex-wrap gap-1.5">
            {['0.1', '0.5', '1', '5', '10'].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setTipAmount(v)}
                className={`rounded-full px-2.5 py-1 text-[11px] ${
                  tipAmount === v ? 'bg-accent/20 text-accent' : 'bg-panel-1 text-dim hover:text-text'
                }`}
              >
                {v} DPB
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-dim">金额</span>
            <Input
              value={tipAmount}
              onChange={(e) => setTipAmount(e.target.value)}
              inputMode="decimal"
              placeholder="自定义"
            />
            <span className="text-[11px] text-dim">备注</span>
            <Input
              value={tipNote}
              onChange={(e) => setTipNote(e.target.value)}
              placeholder="（可选）"
              className="max-w-28"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setTipOpen(false)}>
              取消
            </Button>
            <Button size="sm" loading={busy === 'tip'} onClick={() => void onTip()}>
              打赏
            </Button>
          </div>
        </div>
      ) : null}

      {commentOpen ? <CommentPanel post={post} onClose={() => setCommentOpen(false)} /> : null}

      {previewUrl ? <Lightbox url={previewUrl} onClose={() => setPreviewCid(null)} /> : null}
    </article>
  );
}

/** 远端评论归一化（容错，防整页崩溃）。 */
function normalizeRemoteComment(raw: unknown): CommentItem | null {
  if (raw == null || typeof raw !== 'object') return null;
  const c = raw as Record<string, unknown>;
  const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
  const num = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
  const text = str(c.text).trim();
  if (!text) return null;
  return {
    id: str(c.id) || `c_${num(c.ts, Date.now())}`,
    cid: str(c.postCid) || str(c.cid),
    author: str(c.author) || (typeof c.authorPeer === 'string' ? c.authorPeer.slice(0, 10) : '匿名'),
    text,
    ts: num(c.ts, Date.now()),
  };
}

/** 评论面板：中继历史 + 实时订阅 + P2P 广播发送（每条评论上链存证 kind=comment）。 */
function CommentPanel({ post, onClose }: { post: Post; onClose: () => void }): JSX.Element {
  const cid = post.manifestCid;
  const [items, setItems] = useState<CommentItem[]>(() => {
    try {
      const raw = window.localStorage.getItem(`mesh_comments:${cid}`);
      return raw ? (JSON.parse(raw) as CommentItem[]) : [];
    } catch {
      return [];
    }
  });
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const persistLocal = (key: string, list: CommentItem[]): void => {
    try {
      window.localStorage.setItem(`mesh_comments:${key}`, JSON.stringify(list.slice(-50)));
    } catch {
      /* 存储满则忽略 */
    }
  };

  const mergeComments = (base: CommentItem[], add: (CommentItem | null)[]): CommentItem[] => {
    const map = new Map<string, CommentItem>();
    for (const it of base) map.set(it.id, it);
    for (const it of add) if (it && !map.has(it.id)) map.set(it.id, it);
    return [...map.values()].sort((a, b) => a.ts - b.ts);
  };

  useEffect(() => {
    if (!cid) return;
    let active = true;
    void container.feed
      .commentHistory(cid)
      .then((list) => {
        if (!active) return;
        const merged = mergeComments(itemsRef.current, list.map((raw) => normalizeRemoteComment(raw)));
        setItems(merged);
        persistLocal(cid, merged);
      })
      .catch(() => {
        /* 中继不可达：保留本地缓存 */
      });
    container.feed.commentSubscribe(cid, (comment) => {
      const item = normalizeRemoteComment(comment);
      if (!item) return;
      setItems((prev) => {
        if (prev.some((x) => x.id === item.id)) return prev;
        const next = [...prev, item];
        persistLocal(cid, next);
        return next;
      });
    });
    return () => {
      active = false;
      container.feed.commentUnsubscribe(cid);
    };
  }, [cid]);

  const send = async (): Promise<void> => {
    const trimmed = text.trim();
    if (!trimmed || !cid || sending) return;
    setSending(true);
    const item: CommentItem = {
      id: `c_${Date.now()}`,
      cid,
      author: container.identity.address?.slice(0, 10) || '匿名',
      text: trimmed,
      ts: Date.now(),
    };
    try {
      await container.feed.commentPublish(cid, item);
      useFeedStore.getState().bumpStat(post.id, 'comments');
      useUiStore.getState().pushToast('success', '评论已发送');
    } catch {
      useUiStore.getState().pushToast('error', '中继暂不可用，评论仅保存在本机');
    }
    setItems((prev) => {
      if (prev.some((x) => x.id === item.id)) return prev;
      const next = [...prev, item];
      persistLocal(cid, next);
      return next;
    });
    setText('');
    void anchorKind('comment', cid);
    setSending(false);
  };

  return (
    <div className="border-t border-line bg-panel-1 p-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium text-text">评论 · {post.authorName || '匿名用户'}</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          收起
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="py-2 text-xs text-dim">还没有评论，来抢沙发（评论会上链存证、跨端可见）</p>
      ) : (
        <ul className="mb-2 max-h-64 space-y-2 overflow-y-auto">
          {items.map((it) => (
            <li key={it.id} className="rounded-btn bg-panel-2 px-3 py-2 text-xs">
              <p className="font-medium text-text">{it.author}</p>
              <p className="mt-0.5 whitespace-pre-wrap text-dim">{it.text}</p>
              <p className="mt-1 text-[11px] text-dim">{formatRelativeTime(it.ts)}</p>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void send();
          }}
          placeholder="友善评论，评论即挖矿…"
        />
        <Button size="sm" loading={sending} onClick={() => void send()}>
          发送
        </Button>
      </div>
    </div>
  );
}

/** Feed 筛选 Tab。 */
const FEED_TABS = [
  { key: 'recommend', label: '🔥 推荐' },
  { key: 'latest', label: '🕐 最新' },
  { key: 'friends', label: '👥 好友' },
  { key: 'video', label: '🎬 视频' },
] as const;
type FeedTab = (typeof FEED_TABS)[number]['key'];

/** 广场：移动优先信息流（筛选条 + 折叠发布 + 操作内联计数 + 长文截断 + 相对时间）。 */
export function FeedPage(): JSX.Element {
  const posts = useFeedStore((s) => s.posts);
  const contacts = useChatStore((s) => s.contacts);
  const following = useSocialStore((s) => s.following);
  const selfDid = useIdentityStore((s) => s.did);
  const [tab, setTab] = useState<FeedTab>('recommend');
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [reportTarget, setReportTarget] = useState<string | null>(null);
  const [appealTarget, setAppealTarget] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<'public' | 'friends' | 'private'>('public');
  const [composer, setComposer] = useState<'video' | 'audio' | 'live' | null>(null);
  const [composerText, setComposerText] = useState('');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const mediaRef = useRef<HTMLInputElement>(null);

  const load = useCallback((): void => {
    if (!container.hasPeerClient() || !container.hasContentStore()) return;
    void container.feed.loadHistory().catch((err) => {
      const code = err instanceof MeshLinkError ? err.code : 'E_UNKNOWN';
      useUiStore.getState().pushError(
        code,
        code === 'E_CONTENT_FETCH_TIMEOUT' ? { onAction: () => load() } : undefined,
      );
    });
  }, []);

  useEffect(() => {
    load();
    if (!container.hasPeerClient()) return;
    try {
      container.feed.subscribe(() => undefined);
    } catch {
      /* 未装配静默 */
    }
  }, [load]);

  /** 好友集合（scope=friends 过滤用）。 */
  const friendDids = useMemo(() => {
    const set = new Set<string>();
    for (const c of contacts) if (c.did) set.add(c.did.toLowerCase());
    for (const d of following) set.add(d.toLowerCase());
    if (selfDid) set.add(selfDid.toLowerCase());
    return set;
  }, [contacts, following, selfDid]);

  /** 可见范围过滤。 */
  const visiblePosts = useMemo(() => {
    const me = (selfDid ?? '').toLowerCase();
    return posts.filter((p) => {
      const author = (typeof p.authorDid === 'string' ? p.authorDid : '').toLowerCase();
      const sc = p.scope ?? 'public';
      if (sc === 'private') return me !== '' && author === me;
      if (sc === 'friends') return me !== '' && (author === me || friendDids.has(author));
      return true;
    });
  }, [posts, friendDids, selfDid]);

  /** 筛选：推荐（默认流）/ 最新（时间倒序）/ 好友 / 视频（含媒体的帖子）。 */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = visiblePosts;
    if (tab === 'friends') {
      const me = (selfDid ?? '').toLowerCase();
      list = list.filter((p) => {
        const author = (typeof p.authorDid === 'string' ? p.authorDid : '').toLowerCase();
        return me !== '' && (author === me || friendDids.has(author));
      });
    } else if (tab === 'video') {
      list = list.filter((p) => p.media?.kind === 'video' || p.media?.kind === 'live');
    }
    if (tab === 'latest') list = [...list].sort((a, b) => b.ts - a.ts);
    if (q) {
      list = list.filter(
        (p) => (p.text || '').toLowerCase().includes(q) || (p.authorName || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [visiblePosts, tab, query, friendDids, selfDid]);

  const pickFiles = (list: FileList | null): void => {
    if (!list) return;
    const picked = Array.from(list).filter((f) => f.type.startsWith('image/'));
    if (picked.length > CONTENT_MAX_IMAGES_PER_POST) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      setFiles(picked.slice(0, CONTENT_MAX_IMAGES_PER_POST));
      return;
    }
    setFiles(picked);
  };

  const publish = async (): Promise<void> => {
    if (!text.trim() && files.length === 0) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    setPublishing(true);
    try {
      const post = await container.feed.publish({ text, images: files, scope });
      setText('');
      setFiles([]);
      if (fileRef.current) fileRef.current.value = '';
      markTaskDone('posted');
      useUiStore.getState().pushToast('success', '已发布到广场');
      setComposerOpen(false);
      const kind = files.length > 0 ? 'image' : 'post';
      void anchorKind(kind, post.manifestCid).then((ok) => {
        if (ok) useUiStore.getState().pushToast('success', `已上链锚定（${kind} 挖矿奖励已触发）`);
      });
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setPublishing(false);
    }
  };

  const publishComposer = async (): Promise<void> => {
    if (!composer) return;
    if (composer !== 'live' && !mediaFile) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    if (composer === 'live' && !composerText.trim()) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    setPublishing(true);
    try {
      if (composer === 'live') {
        const livePost = await container.feed.publish({ text: `🔴 ${composerText}` });
        setComposer(null);
        setComposerText('');
        markTaskDone('posted');
        useUiStore.getState().pushToast('success', '直播帖已发布');
        setComposerOpen(false);
        void anchorKind('live', livePost.manifestCid).then((ok) => {
          if (ok) useUiStore.getState().pushToast('success', '已上链锚定（live 挖矿奖励已触发）');
        });
        return;
      }
      const post = await container.feed.publish({
        text: composerText,
        media: { file: mediaFile as File, kind: composer },
      });
      setComposer(null);
      setComposerText('');
      setMediaFile(null);
      if (mediaRef.current) mediaRef.current.value = '';
      markTaskDone('posted');
      useUiStore.getState().pushToast('success', '已发布媒体帖');
      setComposerOpen(false);
      const kind = composer === 'video' ? 'video' : 'audio';
      void anchorKind(kind, post.manifestCid).then((ok) => {
        if (ok) useUiStore.getState().pushToast('success', `已上链锚定（${kind} 挖矿奖励已触发）`);
      });
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setPublishing(false);
    }
  };

  const doSearch = async (): Promise<void> => {
    const q = query.trim();
    if (!q) return;
    const addr = container.identity.address;
    if (addr) {
      void container.chain
        .search({ from: addr as `0x${string}`, termHash: sha256Hex(q), signature: container.identity.signObject({ term: q, ts: Date.now() }) })
        .catch(() => undefined);
    }
    useUiStore.getState().pushToast('success', `搜索「${q}」（已上链记录）`);
  };

  return (
    <div className="pb-20">
      {/* 顶栏：搜索 + 发布 */}
      <div className="sticky top-0 z-20 border-b border-line bg-panel/95 px-3 py-2 backdrop-blur">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void doSearch();
              }}
              placeholder="搜索内容或作者（搜索即挖矿）"
              className="pr-9"
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-dim hover:text-accent"
              onClick={() => void doSearch()}
            >
              🔍
            </button>
          </div>
          <Button
            size="sm"
            variant="primary"
            onClick={() => setComposerOpen((v) => !v)}
          >
            {composerOpen ? '收起' : '＋ 发布'}
          </Button>
        </div>
        {/* 筛选 Tab 条 */}
        <div className="scrollbar-none -mx-3 mt-2 flex gap-1 overflow-x-auto px-3">
          {FEED_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs transition-colors ${
                tab === t.key ? 'bg-accent/15 font-medium text-accent' : 'text-dim hover:text-text'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* 折叠发布面板（点「＋ 发布」展开） */}
      {composerOpen ? (
        <div className="card m-3 space-y-3 p-3">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="分享此刻…（支持配图，最多 6 张）"
            autoFocus
          />
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 rounded-full bg-panel-2 p-1">
              {(
                [
                  { v: 'public', label: '🌍 公开' },
                  { v: 'friends', label: '👥 好友' },
                  { v: 'private', label: '🔒 仅自己' },
                ] as const
              ).map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setScope(o.v)}
                  className={`rounded-full px-2.5 py-1 text-[11px] transition-colors ${
                    scope === o.v ? 'bg-accent/20 text-accent' : 'text-dim hover:text-text'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => pickFiles(e.target.files)}
            />
            <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()}>
              🖼 图片
            </Button>
            <span className="text-[11px] text-dim">{files.length}/{CONTENT_MAX_IMAGES_PER_POST}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setComposer(composer === 'video' ? null : 'video');
                setComposerText('');
                setMediaFile(null);
              }}
            >
              📹 视频
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setComposer(composer === 'audio' ? null : 'audio');
                setComposerText('');
                setMediaFile(null);
              }}
            >
              🎙 语音
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setComposer(composer === 'live' ? null : 'live');
                setComposerText('');
                setMediaFile(null);
              }}
            >
              🔴 直播
            </Button>
          </div>
          {composer ? (
            <div className="space-y-2 border-t border-line pt-2">
              {composer === 'live' ? (
                <Textarea
                  value={composerText}
                  onChange={(e) => setComposerText(e.target.value)}
                  placeholder="直播标题（live 帖上链触发 live 挖矿奖励）"
                />
              ) : (
                <>
                  <input
                    ref={mediaRef}
                    type="file"
                    accept={composer === 'video' ? 'video/*' : 'audio/*'}
                    className="hidden"
                    onChange={(e) => setMediaFile(e.target.files?.[0] ?? null)}
                  />
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => mediaRef.current?.click()}>
                      {composer === 'video' ? '选择视频' : '选择音频'}
                    </Button>
                    {mediaFile ? (
                      <span className="truncate text-[11px] text-dim">
                        {mediaFile.name}（{Math.round(mediaFile.size / 1024)} KB）
                      </span>
                    ) : null}
                  </div>
                  <Textarea
                    value={composerText}
                    onChange={(e) => setComposerText(e.target.value)}
                    placeholder={`${composer === 'video' ? '视频' : '语音'}描述（可选，发布后自动锚定 ${composer} 挖矿奖励）`}
                  />
                </>
              )}
            </div>
          ) : null}
          {files.length > 0 ? (
            <ul className="space-y-1">
              {files.map((f) => (
                <li key={`${f.name}-${f.size}`} className="truncate text-[11px] text-dim">
                  {f.name}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setComposerOpen(false)}>
              收起
            </Button>
            <Button size="sm" loading={publishing} onClick={() => void (composer ? publishComposer() : publish())}>
              发布
            </Button>
          </div>
        </div>
      ) : null}

      {/* 内容流 */}
      {filtered.length === 0 ? (
        <div className="px-3 pt-10">
          <EmptyState
            title={tab === 'video' ? '还没有视频内容' : tab === 'friends' ? '好友还没有动态' : '还没有动态'}
            description={tab === 'friends' ? '去发现页加好友，或发布第一条动态' : '发布你的第一条帖子吧'}
          />
        </div>
      ) : (
        <section className="space-y-3 px-3 pt-3">
          {filtered.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onReport={setReportTarget}
              // 申诉入口仅对当前用户自己的帖子开放（链端仍校验作者身份与下架状态）
              onAppeal={
                selfDid && post.authorDid === `did:dpb:${selfDid.toLowerCase()}`
                  ? setAppealTarget
                  : undefined
              }
            />
          ))}
        </section>
      )}

      {/* 悬浮发布按钮（移动端拇指友好） */}
      <button
        type="button"
        onClick={() => setComposerOpen((v) => !v)}
        className="fixed bottom-20 right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-2xl text-white shadow-lg md:hidden"
        aria-label="发布动态"
      >
        {composerOpen ? '×' : '＋'}
      </button>

      <ReportDialog
        key={reportTarget ?? 'none'}
        open={reportTarget !== null}
        defaultTargetType="post"
        defaultTargetId={reportTarget ?? ''}
        onClose={() => setReportTarget(null)}
      />
      <AppealDialog
        key={appealTarget ?? 'none'}
        open={appealTarget !== null}
        defaultCid={appealTarget ?? ''}
        onClose={() => setAppealTarget(null)}
      />
    </div>
  );
}

export default FeedPage;
